import { snakeToTitleCase } from 'src/utils/String';
import {
  attachCurrency,
  DEFAULT_CURRENCY_CODE,
  getCurrencySymbol,
} from 'src/utils/currency';
import { Metric, GoalStatus, KpiUnit, ModelType } from '../types';

// ============ Metric Formatting ============

/**
 * Whether a metric value is displayed as a euro amount.
 * Performance metrics have fixed units (cpa/spend => €, roi/roas => %);
 * outcome metrics follow the bound model's KPI unit. Legacy metric names
 * (sales/cost) keep their euro formatting so old goals still render.
 */
const isEuroMetric = (metric: Metric, kpiUnit?: KpiUnit | null): boolean => {
  switch (metric) {
    case 'spend':
    case 'cpa':
      return true;
    case 'roi':
    case 'roas':
      return false;
    default:
      // The model's KPI unit wins when known — an outcome metric named
      // 'sales' on a unit-based model is a count, not euros. The name-based
      // guess only covers legacy rows with no unit info.
      if (kpiUnit != null) {
        return kpiUnit === 'euro';
      }
      return metric === 'sales' || metric === 'cost';
  }
};

const isPercentMetric = (metric: Metric): boolean =>
  metric === 'roi' || metric === 'roas';

/**
 * Format a value based on its metric type, using the customer currency for
 * monetary metrics. Example: formatMetricValue(20000, 'sales') => '20,000€',
 * or '$20,000' / '20,000 kr' depending on the customer's currency.
 */
export const formatMetricValue = (
  value: number,
  metric: Metric,
  kpiUnit?: KpiUnit | null,
  currencyCode = DEFAULT_CURRENCY_CODE,
): string => {
  // Ratio metrics on unit-KPI models produce tiny values (e.g. ROAS 0.0042 —
  // subscriptions per euro); two fixed decimals would render them as "0"
  const formatter =
    Math.abs(value) > 0 && Math.abs(value) < 0.01
      ? new Intl.NumberFormat('en-US', { maximumSignificantDigits: 2 })
      : new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

  if (isPercentMetric(metric)) {
    return `${formatter.format(value)}%`;
  }
  if (isEuroMetric(metric, kpiUnit)) {
    return attachCurrency(formatter.format(value), currencyCode);
  }
  return formatter.format(value);
};

/**
 * Get the unit for a metric — the customer currency symbol for monetary
 * metrics. Example: getMetricUnit('spend') => '€' / 'kr' / '$'.
 */
export const getMetricUnit = (
  metric: Metric,
  kpiUnit?: KpiUnit | null,
  currencyCode = DEFAULT_CURRENCY_CODE,
): string => {
  if (isPercentMetric(metric)) {
    return '%';
  }
  if (isEuroMetric(metric, kpiUnit)) {
    return getCurrencySymbol(currencyCode);
  }
  switch (metric) {
    case 'visits':
      return 'visits';
    case 'conversions':
      return 'conversions';
    case 'units_sold':
      return 'units';
    default:
      return '';
  }
};

/**
 * Format target value with metric-appropriate display.
 * Example: formatTargetDisplay(20000, 'sales') => '20,000€' (customer currency)
 * Example: formatTargetDisplay(200, 'roi') => '200% ROI'
 */
export const formatTargetDisplay = (
  value: number,
  metric: Metric,
  kpiUnit?: KpiUnit | null,
  currencyCode = DEFAULT_CURRENCY_CODE,
): string => {
  const formatted = formatMetricValue(value, metric, kpiUnit, currencyCode);

  switch (metric) {
    case 'roi':
      return `${formatted} ROI`;
    case 'roas':
      return `${formatted} ROAS`;
    case 'visits':
      return `${formatted} visits`;
    case 'conversions':
      return `${formatted} conversions`;
    case 'units_sold':
      return `${formatted} units`;
    default:
      return formatted;
  }
};

/**
 * Label shown next to the remaining value on goal cards. For ROI/ROAS the
 * remaining value is a difference of ratios rendered with a % suffix — "to
 * target" keeps it from reading as percent-of-progress. CPA is inverted.
 */
export const getRemainingLabel = (metric: Metric): string => {
  if (metric === 'cpa') {
    return 'to reduce';
  }
  if (isPercentMetric(metric)) {
    return 'to target';
  }
  return 'remaining';
};

// ============ Status Formatting ============

export const STATUS_DISPLAY: Record<GoalStatus, string> = {
  on_track: 'On Track',
  completed: 'Completed',
  behind: 'Behind',
};

export const STATUS_COLORS: Record<GoalStatus, { bg: string; color: string }> =
  {
    on_track: { bg: 'green.50', color: 'green.600' },
    completed: { bg: 'gray.100', color: 'gray.600' },
    behind: { bg: 'red.50', color: 'red.600' },
  };

// ============ Model Type Formatting ============

export const MODEL_TYPE_DISPLAY: Record<ModelType, string> = {
  da: 'Digital Attribution',
  mm: 'Marketing Mix Modelling',
  mo: 'Marketing Optimization',
  lt: 'Long Term Effect',
};

export const MODEL_TYPE_PATHS: Record<ModelType, string> = {
  da: '/digital/overview',
  mm: '/mmm/overview',
  mo: '/marketing-optimization',
  lt: '/longterm-effect',
};

// ============ Metric Options ============

export const PERFORMANCE_METRIC_OPTIONS: { value: Metric; label: string }[] = [
  { value: 'roi', label: 'ROI' },
  { value: 'roas', label: 'ROAS' },
  { value: 'cpa', label: 'CPA' },
  { value: 'spend', label: 'Spend' },
];

export const MODEL_TYPE_OPTIONS: { value: ModelType; label: string }[] = [
  { value: 'da', label: 'Digital Attribution' },
  { value: 'mm', label: 'Marketing Mix Modelling' },
  { value: 'mo', label: 'Marketing Optimization' },
  { value: 'lt', label: 'Long Term Effect' },
];

// ============ Metric Label Formatting ============

/**
 * Get display label for a metric.
 * Performance metrics use their static labels; outcome metrics (the model's
 * KPI metric name) are title-cased, e.g. 'new_subscriptions' => 'New Subscriptions'.
 */
export const formatMetricLabel = (metric: Metric): string => {
  const option = PERFORMANCE_METRIC_OPTIONS.find((opt) => opt.value === metric);
  return option?.label ?? snakeToTitleCase(metric);
};

// ============ Date Formatting ============

/**
 * Format ISO date string to display format.
 * Example: formatGoalDate('2025-08-28') => 'Aug 28, 2025'
 */
export const formatGoalDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};
